import { useEffect, useState } from 'react';
import { Input, Modal } from 'antd';
import { AUTOFILL_OPT_OUT_ATTRS, NO_TEXT_ASSIST } from '../../utils/fieldAssist';

/**
 * A destructive-action confirmation: the user must TYPE THE OPERATION WORD
 * ('delete' / 'archive' / 'transfer') before the primary button arms. The
 * API trusts the client on this, so the dialog is the whole guard.
 *
 * One component for every Danger zone on the platform — a collection's
 * (`CollectionForm`), a card's (`CardDangerZone`), an account's (`Profile`,
 * where the word is the account's email address, and `AdminUsers`) — so the
 * sentence, the placeholder and the arming rule can never drift apart.
 *
 * The password manager may fill a password beside the word (deleting the
 * account, turning off two-step sign-in), so the typed word is what proves a
 * person meant it: the field carries the opt-out markers itself rather than
 * leaning on the page-wide guard (utils/fieldAssist.js).
 *
 * `word` is compared lowercased, so pass it lowercased. `label` is how the
 * word is SHOWN when it is something the person named — a collection's name,
 * which a manager who is not its owner types to delete it (KPR-1267) — so
 * "Garden Club" reads as they wrote it while "garden club" arms it too.
 * `busy` shows the primary button's spinner while the caller's request is in
 * flight.
 */
export default function ConfirmWordModal({
	open,
	word,
	title,
	okText,
	onOk,
	onCancel,
	extraValid = true,
	busy = false,
	label = null,
	children,
}) {
	const [typed, setTyped] = useState('');

	useEffect(() => {
		if (open) setTyped('');
	}, [open]);

	const armed = typed.trim().toLowerCase() === word && extraValid;
	const shown = label || word;

	return (
		<Modal
			open={open}
			title={title}
			okText={okText}
			okButtonProps={{ danger: true, disabled: !armed, loading: busy }}
			onOk={onOk}
			onCancel={onCancel}
			destroyOnClose
		>
			{children}
			<p>
				Type <strong>{shown}</strong> to confirm.
			</p>
			<Input
				{...NO_TEXT_ASSIST}
				{...AUTOFILL_OPT_OUT_ATTRS}
				value={typed}
				onChange={event => setTyped(event.target.value)}
				placeholder={shown}
				aria-label={`Type ${shown} to confirm`}
				onPressEnter={() => { if (armed) onOk(); }}
			/>
		</Modal>
	);
}
